import '../../App.css';
import CreateApi from '../../shared/Settings/appSettings';
import {
    Box, Stack, Typography } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { setAppTheme } from '../../shared/Settings/appTheme';
import { IOSSwitch } from '../../shared/Components/iosSwitch';
import { TournamentEditor } from './TournamentEditor';
import { useCache } from '../../shared/contexts/CacheContext';
import { useAuth } from 'react-oidc-context';
import { EventListSection } from './EventListSection';
import { EventCardSection } from './EventCardSection';
import { eventListCol, eventListHeaderScrollHideSx, eventListScrollbarMatchSx } from './eventListColumns';
import { useTBContext } from '../Contexts/useTBStates';
import { useOpenEvent } from '../hooks/useOpenEvent';
import { NewTournament } from './NewTournament';
import { isEventPast } from '@moment-golf/shared';
import type { TbEventWithId } from '@moment-golf/types';

interface OverviewProps {
    appDims: any;
    compHeights: any;
}

const theme = setAppTheme(1);

const TournamentsManager = ({appDims, compHeights}: OverviewProps) => {

    const auth = useAuth();
    const api = CreateApi();

    const {
        factor,
        eventData,
        setEventData,
        viewEvent,
        setViewEvent,
        viewEventData,
        setViewEventData,
    } = useTBContext();

    const openEvent = useOpenEvent();

    const handleSelectEvent = async (event: any) => {
        await openEvent(event);
        setToggleOverviewView(false);
    };
    
    const { getData, setData, clearData } = useCache();
    const [toggleOverviewView, setToggleOverviewView] = useState(false);
    const [togglePastEvents, setTogglePastEvents] = useState(true);
    const [forceUpdate, setForceUpdate] = useState(false);
    const [innerAppDims, setInnerAppDims] = useState<any>(appDims);
    const [queryEventData, setQueryEventData] = useState(true);
    const eventDataRef = useRef(eventData); // Create a ref to hold the latest eventData

    useEffect(() => {
        eventDataRef.current = eventData; // Update the ref whenever eventData changes
    }, [eventData]);

    useEffect(() => {
            console.log('Querying Event Data');
            fetchEventData()
    }, [queryEventData]);

    useEffect(() => {
        setInnerAppDims(appDims);
    }, [appDims]);

    useEffect(() => {
        const interval = setInterval(() => {
            console.log('Fetching Event Data');
            fetchEventData();
        }, 10 * 60 * 1000); // 10 minutes in milliseconds

        return () => clearInterval(interval); // Cleanup on unmount
    }, []);

    async function fetchEventData() {

        //console.log('Fetching Event Data');
        //console.log('Event Data length', eventDataRef.current.length); // Use the ref here
        //console.log('Reference Date', eventDataRef.current.length > 0 ? new Date(Math.max(...eventDataRef.current.map((event: any) => new Date(event.modifiedAt || '2000-01-01T00:00:00Z').getTime()))).toISOString() : null);

        await api.get('/tb/GetAllUserEvents', {
            params: {
                userID: auth.user?.profile.sub,
                referenceDate: eventDataRef.current.length > 0 ? new Date(Math.max(...eventDataRef.current.map((event: TbEventWithId) => new Date(event.modifiedAt).getTime()))) : null
            }
        })
        .then((res: { data: TbEventWithId[] }) => {
            if (eventDataRef.current.length === 0) {
                setEventData(res.data);
            } else {

                const updatedEventData = [...eventDataRef.current];

                res.data.forEach((newEvent: TbEventWithId) => {
                    const existingEventIndex = updatedEventData.findIndex(event => event._id === newEvent._id);
                    if (existingEventIndex !== -1) {
                        const existing = updatedEventData[existingEventIndex] as any;
                        // Merge slim list fields from newEvent but preserve full detail fields from existing
                        // (GetAllUserEvents returns slim projection; don't overwrite rounds, challenges, etc.)
                        updatedEventData[existingEventIndex] = {
                            ...existing,
                            ...newEvent,
                            courseName: (newEvent as any).courseName ?? existing.courseName,
                            courseID: (newEvent as any).courseID ?? existing.courseID,
                            rounds: existing.rounds?.length ? existing.rounds : ((newEvent as any).rounds ?? []),
                            flighting: existing.flighting ?? (newEvent as any).flighting,
                            eventInfo: existing.eventInfo ?? (newEvent as any).eventInfo,
                        };
                    } else {
                        updatedEventData.push(newEvent);
                    }
                });

                setEventData(updatedEventData);
            }
            //console.log('Event Data Queried', res.data);
        })
        .catch(err => {
            console.error('Error fetching event data', err);
        });
    }

    async function handleDeleteEvent() {

        const totalRegistrations = viewEventData.registrations.length;
        const totalRegistrationsUploaded = viewEventData.registrations.filter((reg: any) => reg.method === 'upload').length;

        if (totalRegistrations > 0 && totalRegistrations > totalRegistrationsUploaded) {

            alert ('This event cannot be deleted because it has registrations that are not uploaded. Please cancel the event instead.');
            return;
        } else if (totalRegistrations > 0) {

            const confirmDelete = window.confirm(`This event has ${totalRegistrations} registrations. Are you sure you want to delete it?`);
            if (confirmDelete) {
                alert('Event deleted.');
            } else {
                alert('Event deletion canceled.');
            }
        } else {
            const confirmDelete = window.confirm(`Are you sure you want to delete this event?`);
            if (confirmDelete) {
                await api.post('/tb/DeleteEvent', {
                    _id: viewEvent?._id
                })
                .then((res: any) => {
                    alert('Event successfully deleted.');
                    setEventData(eventData.filter((existingEvent: any) => existingEvent._id !== viewEvent?._id));
                    setViewEvent(null);
                })
                .catch((err: any) => {
                    console.error(err);
                });
            } else {
                alert('Event deletion canceled.');
            }
        }
    }
 
    const pastEventsCount = eventData.filter((event: any) => isEventPast(event.endDate)).length;
    const yearsPresent = Array.from(new Set(eventData.map((event: any) => event.year)));

    return (
        <Stack id='tb-editor-container' direction={'column'} spacing={0} sx={{ width: '100%', height: '100%', flex: 1, minHeight: 0 }}>
            {viewEvent ? 
                (
                <TournamentEditor 
                    appDims={appDims} 
                    compHeights={compHeights}
                    /* refreshRoundData={RefreshRoundData} */
                    returnToManager={setViewEvent}
                    deleteEvent={handleDeleteEvent}
                />
                ) : (
                <Stack
                    direction="column"
                    spacing={0}
                    sx={{ width: '100%', height: '100%', minHeight: 0, flex: 1 }}
                >
                <Stack
                    direction={'row'}
                    spacing={2}
                    sx={{
                        flexShrink: 0,
                        height: 30 * factor,
                        padding: '0px 20px',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 1,
                    }}
                >
                    <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center' }}>
                        <Stack direction={'row'} spacing={1} sx={{ alignItems: 'center' }}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Cards</Typography>
                            <IOSSwitch
                                checked={toggleOverviewView}
                                onChange={(e) => {
                                    setToggleOverviewView(e.target.checked);
                                }}
                            />
                            <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>List</Typography>
                        </Stack>
                        <NewTournament
                            appDims={appDims}
                            queryEventData={queryEventData}
                            setQueryEventData={setQueryEventData}
                        />
                    </Stack>

                    <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>
                            Past Events{pastEventsCount > 0 ? `: ${pastEventsCount}` : ''}
                        </Typography>
                        <Stack direction={'row'} spacing={1} sx={{ alignItems: 'center' }}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Hide</Typography>
                            <IOSSwitch
                                checked={togglePastEvents}
                                onChange={(e) => {
                                    setTogglePastEvents(e.target.checked);
                                }}
                            />
                            <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Show</Typography>
                        </Stack>
                    </Stack>
                </Stack>
                {!toggleOverviewView ? (
                    <Box sx={{ flex: 1, minHeight: 0, padding: 2, overflow: 'auto' }}>
                        {yearsPresent.length > 0 && yearsPresent.sort((a: any, b: any) => b - a).map((year: any) => (
                            <EventCardSection
                                key={year}
                                year={year}
                                togglePastEvents={togglePastEvents}
                                handleSelectEvent={handleSelectEvent}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box
                        id="tb-overview-stack"
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            padding: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box sx={{ flexShrink: 0, ...eventListHeaderScrollHideSx }}>
                            <Stack
                                id="tb-overview-header-stack"
                                direction="row"
                                spacing={2}
                                sx={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    borderBottom: `1px solid ${theme.palette.primary.main}`,
                                    padding: '8px 15px',
                                    backgroundColor: 'sepia.background',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.eventName }}>Event Name</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.course }}>Course</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.dates }}>Dates</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.rounds }}>Rounds</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.holes }}>Holes</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.field }}>Field</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.playType }}>Play Type</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.privacy }}>Privacy</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, ...eventListCol.status }}>Status</Typography>
                            </Stack>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                ...eventListScrollbarMatchSx,
                            }}
                        >
                            <Stack direction="column" spacing={1} sx={{ width: '100%', pt: 1 }}>
                                {yearsPresent.length > 0 && yearsPresent.sort((a: any, b: any) => b - a).map((year: any) => (
                                    <EventListSection
                                        key={year}
                                        year={year}
                                        togglePastEvents={togglePastEvents}
                                        handleSelectEvent={handleSelectEvent}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    </Box>
                )}
                </Stack>
            )}
        </Stack>
    );
};

export default TournamentsManager;
